import { Alert, Button, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { Drawer, DrawerHeader } from '../../components/drawer';
import { ConfirmationModal } from '../../components/modals/confirmation-modal';
import { AxleResyncScope, Org } from './types';

// Admin pages are Teamworks-staff-only UI, so plain string literals are fine
// here (root AGENTS.md i18n exception).
export type AxleResyncOption = {
  scope: AxleResyncScope;
  label: string;
  /** Shown on hover so CS knows what the resync touches. */
  description: string;
  /**
   * Clicking an option with this flag opens a confirmation popup instead of
   * resyncing immediately. Only the existing profiles resync can be a large
   * query, so CS must confirm before running it.
   */
  hasLargeQueryWarning?: boolean;
};

export const AXLE_RESYNC_OPTIONS: readonly AxleResyncOption[] = [
  {
    scope: 'org',
    label: 'Resync Org',
    description:
      'This resyncs metadata specific to the Org profile. Ie: org logo or org name',
  },
  {
    scope: 'teams',
    label: 'Resync Teams',
    description:
      'This resyncs metadata specific to the Team profiles. Ie: team logo or team name',
  },
  {
    scope: 'newProfiles',
    label: 'Resync New Profiles',
    description:
      "This resyncs profiles that don't already exist in SC and can be active in SC",
  },
  {
    scope: 'existingProfiles',
    label: 'Resync Existing Profiles',
    description: 'This resyncs all profiles that already exist in SC',
    hasLargeQueryWarning: true,
  },
];

/**
 * Manual per-slice Axle resync drawer for an org (SC-5524). Picking an option
 * closes the drawer and fires the scoped resync; the parent owns the
 * mutation, the in-flight spinner and the toasts.
 */
export function ManualAxleResyncDrawer({
  open,
  onClose,
  org,
  onResync,
}: {
  open: boolean;
  onClose: () => void;
  org: Org;
  onResync: (scope: AxleResyncScope) => void;
}) {
  const [confirmingScope, setConfirmingScope] =
    useState<AxleResyncScope | null>(null);

  return (
    <Drawer open={open} onClose={onClose}>
      <DrawerHeader onClose={onClose}>
        <Typography variant="h3">Manual Axle Resync</Typography>
        <Typography variant="body2">{org.name}</Typography>
      </DrawerHeader>
      <div className="flex flex-col gap-4 px-6 pb-6">
        <Alert severity="warning">
          These resyncs do not recompute app access rules.
        </Alert>
        {AXLE_RESYNC_OPTIONS.map((option) => (
          <Tooltip
            key={option.scope}
            title={option.description}
            placement="left"
          >
            <Button
              variant="outlined"
              className="w-full justify-start"
              onClick={() => {
                if (option.hasLargeQueryWarning) {
                  setConfirmingScope(option.scope);
                } else {
                  onResync(option.scope);
                }
              }}
            >
              {option.label}
            </Button>
          </Tooltip>
        ))}
      </div>
      <ConfirmationModal
        id="manual-axle-resync-large-query-warning"
        open={confirmingScope !== null}
        title="Large query warning"
        message="Warning: resyncing existing profiles could be a large query. Only run this resync when it is needed."
        confirmText="Confirm"
        cancelText="Cancel"
        onCancel={() => setConfirmingScope(null)}
        onConfirm={() => {
          const scope = confirmingScope;
          setConfirmingScope(null);
          if (scope !== null) {
            onResync(scope);
          }
        }}
      />
    </Drawer>
  );
}
